import { apiClient } from '@/utils/api-client'

const API_INTERNAL_BASE = '/reports'

/**
 * Report service for analytics endpoints.
 */
class ReportService {
    /**
     * Get the stale flags report for an environment.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {Object} [params] - Optional filters
     * @param {number} [params.staleAfterDays] - Only include flags not evaluated within this many days (30, 60, or 90)
     * @param {number} [params.page] - Page of the flags list to fetch (1-indexed)
     * @param {number} [params.perPage] - Number of flags per page
     * @param {Object} [options] - Request options
     * @param {AbortSignal} [options.signal] - Signal to cancel this request
     * @returns {Promise<Object>} Stale flags response
     */
    async getStaleFlags(
        projectKey,
        environmentKey,
        params = {},
        { signal } = {}
    ) {
        const response = await apiClient.get(
            API_INTERNAL_BASE +
                '/' +
                projectKey +
                '/' +
                environmentKey +
                '/stale-flags',
            {
                params: {
                    stale_after_days: params.staleAfterDays,
                    page: params.page,
                    per_page: params.perPage,
                },
                signal,
            }
        )
        return response.data
    }

    /**
     * Get the missing default value report for an environment.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {Object} [params] - Optional filters
     * @param {string} [params.environmentKey] - Switch the report to a different environment in the project
     * @param {string} [params.usageStatus] - Narrow to a single usage status ("in_use" or "never_used")
     * @param {string} [params.sortBy] - "name", "key", or "last_used_at"
     * @param {string} [params.sortDir] - "asc" or "desc"
     * @param {number} [params.page] - Page of the flags list to fetch (1-indexed)
     * @param {number} [params.perPage] - Number of flags per page
     * @param {Object} [options] - Request options
     * @param {AbortSignal} [options.signal] - Signal to cancel this request
     * @returns {Promise<Object>} Missing default value response
     */
    async getMissingDefaultValue(
        projectKey,
        environmentKey,
        params = {},
        { signal } = {}
    ) {
        const response = await apiClient.get(
            API_INTERNAL_BASE +
                '/' +
                projectKey +
                '/' +
                environmentKey +
                '/missing-default-value',
            {
                params: {
                    environment_key: params.environmentKey,
                    usage_status: params.usageStatus,
                    sort_by: params.sortBy,
                    sort_dir: params.sortDir,
                    page: params.page,
                    per_page: params.perPage,
                },
                signal,
            }
        )
        return response.data
    }

    /**
     * Get the flag evaluation volume report.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {Object} [params] - Optional filters
     * @param {string} [params.startDate] - Start date (YYYY-MM-DD)
     * @param {string} [params.endDate] - End date (YYYY-MM-DD)
     * @param {string} [params.granularity] - "day", "week", or "month"
     * @param {string} [params.environmentKey] - Narrow the report to a single environment
     * @param {Object} [options] - Request options
     * @param {AbortSignal} [options.signal] - Signal to cancel this request
     * @returns {Promise<Object>} Evaluation volume response
     */
    async getEvaluationVolume(
        projectKey,
        environmentKey,
        params = {},
        { signal } = {}
    ) {
        const response = await apiClient.get(
            API_INTERNAL_BASE +
                '/' +
                projectKey +
                '/' +
                environmentKey +
                '/evaluation-volume',
            {
                params: {
                    start_date: params.startDate,
                    end_date: params.endDate,
                    granularity: params.granularity,
                    environment_key: params.environmentKey,
                },
                signal,
            }
        )
        return response.data
    }

    /**
     * Get the flag changes over time report.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {Object} [params] - Optional filters
     * @param {string} [params.startDate] - Start date (YYYY-MM-DD)
     * @param {string} [params.endDate] - End date (YYYY-MM-DD)
     * @param {string} [params.granularity] - "day", "week", or "month"
     * @param {string} [params.environmentKey] - Narrow the report to a single environment
     * @param {string} [params.flagKey] - Narrow the report to a single flag
     * @param {string} [params.actorUlid] - Narrow the report to a single author
     * @param {number} [params.page] - Page of the events list to fetch (1-indexed)
     * @param {number} [params.perPage] - Number of events per page
     * @param {Object} [options] - Request options
     * @param {AbortSignal} [options.signal] - Signal to cancel this request
     * @returns {Promise<Object>} Flag change timeline response
     */
    async getFlagChangeTimeline(
        projectKey,
        environmentKey,
        params = {},
        { signal } = {}
    ) {
        const response = await apiClient.get(
            API_INTERNAL_BASE +
                '/' +
                projectKey +
                '/' +
                environmentKey +
                '/flag-changes-timeline',
            {
                params: {
                    start_date: params.startDate,
                    end_date: params.endDate,
                    granularity: params.granularity,
                    environment_key: params.environmentKey,
                    flag_key: params.flagKey,
                    actor_ulid: params.actorUlid,
                    page: params.page,
                    per_page: params.perPage,
                },
                signal,
            }
        )
        return response.data
    }

    /**
     * Get the user exposure by segment report.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {Object} [params] - Optional filters
     * @param {string} [params.startDate] - Start date (YYYY-MM-DD)
     * @param {string} [params.endDate] - End date (YYYY-MM-DD)
     * @param {string} [params.flagKey] - Narrow the report to a single flag
     * @param {number} [params.page] - Page of the segments list to fetch (1-indexed)
     * @param {number} [params.perPage] - Number of segments per page
     * @param {Object} [options] - Request options
     * @param {AbortSignal} [options.signal] - Signal to cancel this request
     * @returns {Promise<Object>} User exposure by segment response
     */
    async getUserExposureBySegment(
        projectKey,
        environmentKey,
        params = {},
        { signal } = {}
    ) {
        const response = await apiClient.get(
            API_INTERNAL_BASE +
                '/' +
                projectKey +
                '/' +
                environmentKey +
                '/user-exposure-by-segment',
            {
                params: {
                    start_date: params.startDate,
                    end_date: params.endDate,
                    flag_key: params.flagKey,
                    page: params.page,
                    per_page: params.perPage,
                },
                signal,
            }
        )
        return response.data
    }

    /**
     * Get the flag status overview report.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {Object} [params] - Optional filters
     * @param {string} [params.environmentKey] - Switch the report to a different environment in the project
     * @param {string} [params.status] - Narrow to a single status ("permanent", "temporary", "partial_rollout", "archived")
     * @param {number} [params.minAgeDays] - Only include flags at least this many days old
     * @param {number} [params.maxAgeDays] - Only include flags at most this many days old
     * @param {string} [params.sortBy] - "name", "key", "status", "updated_at", or "age"
     * @param {string} [params.sortDir] - "asc" or "desc"
     * @param {number} [params.page] - Page of the flags list to fetch (1-indexed)
     * @param {number} [params.perPage] - Number of flags per page
     * @param {Object} [options] - Request options
     * @param {AbortSignal} [options.signal] - Signal to cancel this request
     * @returns {Promise<Object>} Flag status overview response
     */
    async getFlagStatusOverview(
        projectKey,
        environmentKey,
        params = {},
        { signal } = {}
    ) {
        const response = await apiClient.get(
            API_INTERNAL_BASE +
                '/' +
                projectKey +
                '/' +
                environmentKey +
                '/flag-status-overview',
            {
                params: {
                    environment_key: params.environmentKey,
                    status: params.status,
                    min_age_days: params.minAgeDays,
                    max_age_days: params.maxAgeDays,
                    sort_by: params.sortBy,
                    sort_dir: params.sortDir,
                    page: params.page,
                    per_page: params.perPage,
                },
                signal,
            }
        )
        return response.data
    }

    /**
     * Get the rollout progress report.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {Object} [params] - Optional filters
     * @param {string} [params.environmentKey] - Narrow the report to a single environment
     * @param {string} [params.flagKey] - Narrow the report to a single flag
     * @param {string} [params.status] - Narrow to a single rollout status ("active" or "paused")
     * @param {number} [params.page] - Page of the active rollouts list to fetch (1-indexed)
     * @param {number} [params.perPage] - Number of rollouts per page
     * @param {Object} [options] - Request options
     * @param {AbortSignal} [options.signal] - Signal to cancel this request
     * @returns {Promise<Object>} Rollout progress response
     */
    async getRolloutProgress(
        projectKey,
        environmentKey,
        params = {},
        { signal } = {}
    ) {
        const response = await apiClient.get(
            API_INTERNAL_BASE +
                '/' +
                projectKey +
                '/' +
                environmentKey +
                '/rollout-progress',
            {
                params: {
                    environment_key: params.environmentKey,
                    flag_key: params.flagKey,
                    status: params.status,
                    page: params.page,
                    per_page: params.perPage,
                },
                signal,
            }
        )
        return response.data
    }

    /**
     * Get the flag ownership & coverage report for a project environment.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {Object} [params] - Optional filters
     * @param {string} [params.teamUlid] - Narrow to flags owned by a member of this team
     * @param {string} [params.ownershipStatus] - Narrow to a single ownership status ("owned" or "unowned")
     * @param {string} [params.sortBy] - "name", "key", or "owner"
     * @param {string} [params.sortDir] - "asc" or "desc"
     * @param {number} [params.page] - Page of the flags list to fetch (1-indexed)
     * @param {number} [params.perPage] - Number of flags per page
     * @param {Object} [options] - Request options
     * @param {AbortSignal} [options.signal] - Signal to cancel this request
     * @returns {Promise<Object>} Flag ownership & coverage response
     */
    async getFlagOwnershipCoverage(
        projectKey,
        environmentKey,
        params = {},
        { signal } = {}
    ) {
        const response = await apiClient.get(
            API_INTERNAL_BASE +
                '/' +
                projectKey +
                '/' +
                environmentKey +
                '/flag-ownership-coverage',
            {
                params: {
                    team_ulid: params.teamUlid,
                    ownership_status: params.ownershipStatus,
                    sort_by: params.sortBy,
                    sort_dir: params.sortDir,
                    page: params.page,
                    per_page: params.perPage,
                },
                signal,
            }
        )
        return response.data
    }

    /**
     * Get the geographic exposure report.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {Object} [params] - Optional filters
     * @param {string} [params.startDate] - Start date (YYYY-MM-DD)
     * @param {string} [params.endDate] - End date (YYYY-MM-DD)
     * @param {string} [params.flagKey] - Narrow the report to a single flag
     * @param {Object} [options] - Request options
     * @param {AbortSignal} [options.signal] - Signal to cancel this request
     * @returns {Promise<Object>} Geographic exposure response
     */
    async getGeographicExposure(
        projectKey,
        environmentKey,
        params = {},
        { signal } = {}
    ) {
        const response = await apiClient.get(
            API_INTERNAL_BASE +
                '/' +
                projectKey +
                '/' +
                environmentKey +
                '/geographic-exposure',
            {
                params: {
                    start_date: params.startDate,
                    end_date: params.endDate,
                    flag_key: params.flagKey,
                },
                signal,
            }
        )
        return response.data
    }
}

export default new ReportService()
